import {
  Button,
  SearchField,
  ToggleButton,
  ToggleButtonGroup,
} from "@heroui/react";
import { useMemo } from "react";

import { GeoFilterPanel } from "@/src/components/GeoFilterPanel";
import { useTranslations } from "@/src/i18n/i18n";
import type {
  EventSort,
  EventStatusFilter,
} from "@/src/pages/Events/events.type";
import {
  DEFAULT_RADIUS,
  isEventSort,
  isEventStatusFilter,
  SORTS,
  STATUS_FILTERS,
} from "@/src/pages/Events/events.type";
import type { EventFilters as Filters } from "@/src/pages/Events/hooks/useEventFilters";

type EventFiltersProps = {
  filters: Filters;
  term: string;
  onTermChange: (_term: string) => void;
  onChange: (_patch: Partial<Filters>) => void;
};

const SORT_KEY = {
  date: "sort-date",
  createdAt: "sort-created",
  name: "sort-name",
  distance: "sort-distance",
} as const satisfies Record<EventSort, string>;

const STATUS_KEY = {
  all: "filter-all",
  open: "status-open",
  complete: "status-complete",
  close: "status-close",
} as const satisfies Record<EventStatusFilter, string>;

/**
 * La barre de filtres : texte, statut, tri, et une recherche géographique
 * repliable — le même [`GeoFilterPanel`](src/components/GeoFilterPanel.tsx) que
 * la liste des comptes, puisque les deux routes prennent le même trio
 * `lat`/`lng`/`radius`.
 *
 * ⚠️ **Le tri par distance est désactivé sans lieu choisi.** Le serveur le
 * refuse en 400 — à juste titre : sans centre, `distance` vaut `null` partout
 * et ne range rien. Un onglet cliquable qui ferait échouer la requête vaudrait
 * moins qu'un onglet éteint qui dit pourquoi.
 */
export function EventFilters({
  filters,
  term,
  onTermChange,
  onChange,
}: EventFiltersProps) {
  const t = useTranslations("events");
  const ta = useTranslations("a11y");

  const hasCenter = filters.lat !== null;

  const sortKeys = useMemo(() => [filters.sort], [filters.sort]);
  const statusKeys = useMemo(() => [filters.status], [filters.status]);

  return (
    <div className="flex flex-col gap-3">
      <SearchField
        aria-label={ta("search-events")}
        fullWidth
        value={term}
        onChange={onTermChange}
      >
        <SearchField.Group>
          <SearchField.SearchIcon />
          <SearchField.Input placeholder={t("search-placeholder")} />
          {/* ⚠️ Le bouton d'effacement de HeroUI s'annonce « Clear » en anglais
              en dur — même piège que le `CloseButton`. */}
          <SearchField.ClearButton aria-label={ta("clear-search")} />
        </SearchField.Group>
      </SearchField>

      <ToggleButtonGroup
        aria-label={ta("filter-events")}
        disallowEmptySelection
        selectedKeys={statusKeys}
        selectionMode="single"
        size="sm"
        onSelectionChange={(keys) => {
          for (const key of keys) {
            if (isEventStatusFilter(key)) onChange({ status: key });
          }
        }}
      >
        {STATUS_FILTERS.map((status) => (
          <ToggleButton id={status} key={status}>
            {t(STATUS_KEY[status])}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-3">
          <ToggleButtonGroup
            aria-label={ta("sort-events")}
            disallowEmptySelection
            selectedKeys={sortKeys}
            selectionMode="single"
            size="sm"
            onSelectionChange={(keys) => {
              for (const key of keys) {
                if (isEventSort(key)) onChange({ sort: key });
              }
            }}
          >
            {SORTS.map((sort) => (
              <ToggleButton
                id={sort}
                isDisabled={sort === "distance" && !hasCenter}
                key={sort}
              >
                {t(SORT_KEY[sort])}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {/* Le sens du tri est une bascule, pas un onglet de plus : il ne
              change pas *ce* qu'on trie, seulement par quel bout. */}
          <Button
            size="sm"
            variant="ghost"
            onPress={() =>
              onChange({ order: filters.order === "asc" ? "desc" : "asc" })
            }
          >
            {t(filters.order === "asc" ? "order-asc" : "order-desc")}
          </Button>
        </div>

        {/*
          ⚠️ **Une phrase, et non une infobulle sur le bouton éteint.** React
          Aria coupe les événements de survol d'un `ToggleButton` désactivé :
          l'infobulle ne s'ouvrirait jamais, et l'onglet resterait éteint sans
          explication. Ici la raison est lisible sans rien viser.
        */}
        {hasCenter ? null : (
          <p className="text-xs text-muted">{t("sort-distance-hint")}</p>
        )}
      </div>

      <GeoFilterPanel
        hasCenter={hasCenter}
        place={filters.near}
        radius={filters.radius}
        onClear={() =>
          onChange({
            lat: null,
            lng: null,
            near: "",
            radius: DEFAULT_RADIUS,
            // Le tri par distance n'a plus de centre : le garder ferait
            // refuser la requête suivante par le serveur.
            ...(filters.sort === "distance" ? { sort: "date" as const } : {}),
          })
        }
        onRadiusChange={(radius) => onChange({ radius })}
        onSelect={(place) =>
          onChange({
            lat: place.latitude,
            lng: place.longitude,
            near: place.label,
          })
        }
      />
    </div>
  );
}
